import { ref, computed, onUnmounted } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { searchProjects } from '../api/projects';
import type { BillingTarget } from '../api/draft';
import type { TimesheetRow } from '../api/client';
import type { ComboOption } from '../components/SearchCombobox.vue';

// Project side of the SearchCombobox, shared by the wizard, the inline row
// editor and the cell-edit card (ADR-0042). Turns the field's query into the
// combobox `options`: backend search once ≥2 chars are typed, deduped recents
// (by WBS1) before that, both ordered by memory ranking (B.9). The host wires
// `setQuery` to the field's `update:query` and `resolve` to its `select`.

const SEP = String.fromCharCode(0x1f);
const UNRANKED = Number.MAX_SAFE_INTEGER;

export interface ProjectPick {
  // Combobox option id — the WBS1 for a project, `cat:<code>` for a time
  // category (so a category never collides with a same-named project).
  id: string;
  wbs1: string;
  wbs1Name: string;
  isPhased: boolean;
  // VP time-category code (ADR-0044) — set only when this pick is a category.
  category?: string;
  // Present when chosen from recents — may already carry a resolved leaf so the
  // host can pre-seed the cascade.
  recent?: BillingTarget;
  // Idle grouping (resolve mode): 'suggested' = the item's candidate targets,
  // pinned above the user's 'recent' picks under their own heading. Unset while
  // searching and when there are no suggestions, so the list stays flat.
  group?: 'suggested' | 'recent';
}

export interface UseProjectSearchOptions {
  recent: () => BillingTarget[];
  // Resolve mode: the item's candidate targets (the agent's suggestions), pinned
  // above recents under a "Suggested" heading. Empty in create/edit.
  suggested?: () => BillingTarget[];
  rankedTargetIds: () => string[];
  // Gate the backend query to when the field's host is actually open.
  enabled: () => boolean;
  // On-sheet rows: phased-ness evidence for recents that carry no wbs2.
  timesheetRows?: () => TimesheetRow[];
  // The tenant's VP time categories (ADR-0044) — always offered as picks so any
  // event can be allocated to a category, not only the ones the engine matched.
  categories?: () => BillingTarget[];
}

export function useProjectSearch(opts: UseProjectSearchOptions) {
  const query = ref('');
  const debounced = ref('');
  let timer: ReturnType<typeof setTimeout> | null = null;

  function setQuery(v: string): void {
    query.value = v;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { debounced.value = v; }, 250);
  }

  const searching = computed(() => query.value.trim().length >= 2);
  const searchEnabled = computed(() => opts.enabled() && debounced.value.trim().length >= 2);

  const { data, isFetching } = useQuery({
    queryKey: computed(() => ['projects', 'search', debounced.value]),
    queryFn: () => searchProjects(debounced.value),
    enabled: searchEnabled,
    placeholderData: (prev) => prev,
    retry: false,
    staleTime: 30_000,
  });

  const rankByWbs1 = computed((): Map<string, number> => {
    const m = new Map<string, number>();
    opts.rankedTargetIds().forEach((id, i) => {
      const wbs1 = id.split(SEP)[0];
      if (!m.has(wbs1)) m.set(wbs1, i);
    });
    return m;
  });

  // A category is offered by name alone (no WBS code column), so a nameless one
  // is an invisible row. Its code IS its display name in VP's band row, which is
  // where every category target ultimately comes from — so fall back to it.
  const categoryPick = (c: BillingTarget): ProjectPick => {
    const id = `cat:${c.category}`;
    return {
      id, wbs1: c.wbs1, wbs1Name: c.wbs1Name || c.wbs1,
      isPhased: false, category: c.category, recent: c,
    };
  };

  const idlePick = (t: BillingTarget, rows: TimesheetRow[]): ProjectPick => {
    if (t.category) return categoryPick(t);
    const phased = !!t.wbs2 || rows.some((r) => r.wbs1 === t.wbs1 && r.wbs2);
    return { id: t.wbs1, wbs1: t.wbs1, wbs1Name: t.wbs1Name, isPhased: phased, recent: t };
  };

  // Option id → pick payload for the current query. Deduped: the first entry for
  // an id wins, so a project appears once whether from search or recents. Time
  // categories (Holiday, PTO, …) surface only when relevant — matched by name
  // while searching, or offered as a candidate/recent when idle — never pinned to
  // the idle list, which stays the user's own recents + this item's candidates.
  const picks = computed((): Map<string, ProjectPick> => {
    const m = new Map<string, ProjectPick>();
    const q = query.value.trim().toLowerCase();
    if (searching.value) {
      for (const c of opts.categories?.() ?? []) {
        if (!c.wbs1Name.toLowerCase().includes(q)) continue;
        m.set(`cat:${c.category}`, categoryPick(c));
      }
      for (const p of data.value?.projects ?? []) {
        if (!m.has(p.wbs1)) m.set(p.wbs1, { id: p.wbs1, wbs1: p.wbs1, wbs1Name: p.wbs1Name, isPhased: !!p.isPhased });
      }
      return m;
    }
    // Idle: the user's recents, plus — resolving a needs-input item — the item's
    // candidate targets pinned first under a "Suggested" heading. Grouping only
    // kicks in when there are suggestions; otherwise the list stays flat (no lone
    // "Recent" heading in create/edit). Suggested added first so a target that is
    // both a candidate and a recent shows once, under Suggested.
    const rows = opts.timesheetRows?.() ?? [];
    const suggested = opts.suggested?.() ?? [];
    const grouped = suggested.length > 0;
    const addIdle = (t: BillingTarget, group?: 'suggested' | 'recent'): void => {
      const p = idlePick(t, rows);
      if (m.has(p.id)) return;
      m.set(p.id, group ? { ...p, group } : p);
    };
    for (const t of suggested) addIdle(t, grouped ? 'suggested' : undefined);
    for (const t of opts.recent()) addIdle(t, grouped ? 'recent' : undefined);
    return m;
  });

  // Suggested picks sort ahead of recents; within a group, memory rank then
  // insertion order. Ungrouped picks (searching, or create/edit idle) all share
  // rank 1, so this collapses to the plain rank sort.
  const groupRank = (g?: string): number => (g === 'suggested' ? 0 : 1);

  const options = computed((): ComboOption[] =>
    [...picks.value.values()]
      .map((p, i) => ({ p, i }))
      .sort((a, b) =>
        groupRank(a.p.group) - groupRank(b.p.group)
        || (rankByWbs1.value.get(a.p.wbs1) ?? UNRANKED) - (rankByWbs1.value.get(b.p.wbs1) ?? UNRANKED)
        || a.i - b.i)
      // A category shows its name only (no WBS code column); a project shows both.
      .map(({ p }) => {
        const base = p.category ? { id: p.id, name: p.wbs1Name } : { id: p.id, code: p.wbs1, name: p.wbs1Name };
        return p.group ? { ...base, group: p.group } : base;
      }),
  );

  const loading = computed(() => searchEnabled.value && isFetching.value);
  const emptyLabel = computed(() =>
    searching.value ? 'No active projects found.' : 'Type at least 2 characters to search.',
  );

  function resolve(id: string): ProjectPick | null {
    return picks.value.get(id) ?? null;
  }

  function reset(): void {
    query.value = '';
    debounced.value = '';
  }

  onUnmounted(() => { if (timer) clearTimeout(timer); });

  return { query, setQuery, options, loading, emptyLabel, resolve, reset };
}
